/*
 * File: store.ts
 * Project: beaker
 * -----
 * Last Modified: 15 Mar 2022 10:33:40 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { AnyAction, configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { abbyCadabbyApi, abbyCadabbyDivisionsParams } from './services/abbyCadabby';
import { animalsApi, animalsConditionsParams, animalsDispositionsParams, animalsOwnersParams } from './services/animals';
import { beautifulDayMonsterApi, beautifulDayMonsterCustomersParams } from './services/beautifulDayMonster';
import { chuckieSueAddressesApi, chuckieSueAddressesParams, chuckieSueWorldsApi } from './services/chuckieSue';
import { cookieMonsterApi } from './services/cookieMonster';
import { donaldGrumpApi, donaldGrumpProductMastersParams, donaldGrumpReleasedProductsParams } from './services/donaldGrump';
import { elmoApi, elmoDeploymentRequestsParams } from './services/elmo';
import { freddyCadabbyApi } from './services/freddyCadabby';
import { juliaApi, juliaPriceBookEntryParams, juliaProductCatalogParams } from './services/julia';
import { mrsGrouchApi } from './services/mrsGrouch/mrsGrouchApi';
import { rudyCadabbyApi } from './services/rudyCadabby/rudyCadabbyApi';
import { appSlice, assignedRulesSlice, businessListSlice, formSlice, importSlice, productSearchSlice } from './slices';

export const store = configureStore({
  reducer: {
    [appSlice.name]: appSlice.reducer,
    [formSlice.name]: formSlice.reducer,
    [productSearchSlice.name]: productSearchSlice.reducer,
    [businessListSlice.name]: businessListSlice.reducer,
    [assignedRulesSlice.name]: assignedRulesSlice.reducer,
    [importSlice.name]: importSlice.reducer,
    // Elmo
    [elmoApi.reducerPath]: elmoApi.reducer,
    [elmoDeploymentRequestsParams.name]: elmoDeploymentRequestsParams.reducer,
    // Chuckie Sue
    [chuckieSueAddressesApi.reducerPath]: chuckieSueAddressesApi.reducer,
    [chuckieSueAddressesParams.name]: chuckieSueAddressesParams.reducer,
    [chuckieSueWorldsApi.reducerPath]: chuckieSueWorldsApi.reducer,
    // Cookie Monster
    [cookieMonsterApi.reducerPath]: cookieMonsterApi.reducer,
    // Julia
    [juliaApi.reducerPath]: juliaApi.reducer,
    [juliaProductCatalogParams.name]: juliaProductCatalogParams.reducer,
    [juliaPriceBookEntryParams.name]: juliaPriceBookEntryParams.reducer,
    // Abby Cadabby
    [abbyCadabbyApi.reducerPath]: abbyCadabbyApi.reducer,
    [abbyCadabbyDivisionsParams.name]: abbyCadabbyDivisionsParams.reducer,
    // Mrs Grouch
    [mrsGrouchApi.reducerPath]: mrsGrouchApi.reducer,
    // Beautiful Day Monster
    [beautifulDayMonsterApi.reducerPath]: beautifulDayMonsterApi.reducer,
    [beautifulDayMonsterCustomersParams.name]: beautifulDayMonsterCustomersParams.reducer,
    // Donald Grump
    [donaldGrumpApi.reducerPath]: donaldGrumpApi.reducer,
    [donaldGrumpProductMastersParams.name]: donaldGrumpProductMastersParams.reducer,
    [donaldGrumpReleasedProductsParams.name]: donaldGrumpReleasedProductsParams.reducer,
    // Animals
    [animalsApi.reducerPath]: animalsApi.reducer,
    [animalsOwnersParams.name]: animalsOwnersParams.reducer,
    [animalsConditionsParams.name]: animalsConditionsParams.reducer,
    [animalsDispositionsParams.name]: animalsDispositionsParams.reducer,
    // Rudy Cadabby
    [rudyCadabbyApi.reducerPath]: rudyCadabbyApi.reducer,
    // Freddy Cadabby
    [freddyCadabbyApi.reducerPath]: freddyCadabbyApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      elmoApi.middleware,
      cookieMonsterApi.middleware,
      chuckieSueAddressesApi.middleware,
      chuckieSueWorldsApi.middleware,
      juliaApi.middleware,
      abbyCadabbyApi.middleware,
      mrsGrouchApi.middleware,
      beautifulDayMonsterApi.middleware,
      donaldGrumpApi.middleware,
      animalsApi.middleware,
      rudyCadabbyApi.middleware,
      freddyCadabbyApi.middleware
    ])
});

export type ReduxState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type ThunkAppDispatch = ThunkDispatch<ReduxState, void, AnyAction>;
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<ReduxState> = useSelector;

setupListeners(store.dispatch);
export default store;
